import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Download,
  TrendingUp,
  TrendingDown,
  Calculator,
  FileText,
  Receipt,
  ShoppingCart,
  BarChart3,
  PieChart,
  Sparkles,
  Calendar,
  IndianRupee,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from "recharts";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [report, setReport] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [activeTab, setActiveTab] = useState("gst");

  useEffect(() => {
    loadAvailableMonths();
    loadFinancialData();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadMonthlyReport(selectedMonth);
    }
  }, [selectedMonth]);

  const loadAvailableMonths = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/reports/months`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.months.length > 0) {
        setMonths(response.data.months);
        setSelectedMonth(response.data.months[0]);
      }
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load months");
      setLoading(false);
    }
  };

  const loadMonthlyReport = async (month) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/reports/monthly?month=${month}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setReport(response.data);
    } catch (error) {
      toast.error("Failed to load report");
    }
  };

  const loadFinancialData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/reports/financial-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinancialData(response.data);
    } catch (error) {
      console.error("Failed to load financial data:", error);
    }
  };

  const downloadReport = () => {
    if (!report) return;

    const csvContent = generateReportCSV(report);
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `GST_Report_${selectedMonth}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Report downloaded successfully");
  };

  const generateReportCSV = (data) => {
    let csv = `GST Reconciliation Report - ${selectedMonth}\n\n`;
    csv += `Category,Count,Taxable Amount,GST Amount\n`;
    csv += `Purchase Invoices,${data.purchase_invoices},${data.total_purchase_amount - data.total_purchase_gst},${data.total_purchase_gst}\n`;
    csv += `Sales Invoices,${data.sales_invoices},${data.total_sales_amount - data.total_sales_gst},${data.total_sales_gst}\n\n`;
    csv += `GST Component,CGST,SGST,IGST\n`;
    csv += `Purchase GST,${data.purchase_tax_breakdown?.cgst || 0},${data.purchase_tax_breakdown?.sgst || 0},${data.purchase_tax_breakdown?.igst || 0}\n`;
    csv += `Sales GST,${data.sales_tax_breakdown?.cgst || 0},${data.sales_tax_breakdown?.sgst || 0},${data.sales_tax_breakdown?.igst || 0}\n\n`;
    csv += `Net GST Payable/Receivable,,,${data.net_gst_payable}\n\n`;

    csv += `\nPurchase GST Breakdown by Rate\n`;
    csv += `Rate,Count,Taxable Amount,GST Amount,CGST,SGST,IGST\n`;
    Object.entries(data.gst_breakdown.purchase).forEach(([rate, values]) => {
      csv += `${rate}%,${values.count},${values.taxable_amount},${values.gst_amount},${values.cgst || 0},${values.sgst || 0},${values.igst || 0}\n`;
    });

    csv += `\nSales GST Breakdown by Rate\n`;
    csv += `Rate,Count,Taxable Amount,GST Amount,CGST,SGST,IGST\n`;
    Object.entries(data.gst_breakdown.sales).forEach(([rate, values]) => {
      csv += `${rate}%,${values.count},${values.taxable_amount},${values.gst_amount},${values.cgst || 0},${values.sgst || 0},${values.igst || 0}\n`;
    });

    return csv;
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-IN", {
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}`;
  };

  const renderTaxSplit = (breakdown, toneClass) => (
    <div className="grid grid-cols-3 gap-2 mt-2">
      {[
        { label: "CGST", key: "cgst" },
        { label: "SGST", key: "sgst" },
        { label: "IGST", key: "igst" },
      ].map((item) => (
        <div key={item.key} className="rounded-md bg-white/20 px-2 py-1.5">
          <div className="text-[10px] text-white/80">{item.label}</div>
          <div className={`text-xs font-semibold ${toneClass}`}>
            {formatCurrency(breakdown?.[item.key] || 0)}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="text-gray-500 text-sm">Loading financial reports...</p>
        </div>
      </Layout>
    );
  }

  if (months.length === 0 && !financialData?.monthly_data?.length) {
    return (
      <Layout>
        <div className="space-y-4">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="text-xs h-7 border-gray-300 hover:border-gray-400"
          >
            <ArrowLeft size={10} className="mr-1.5" />
            Back to Dashboard
          </Button>
          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-6 text-center">
              <div className="p-3 rounded-full bg-gradient-to-br from-primary-50 to-orange-50 inline-block mb-3">
                <BarChart3 className="text-primary-600" size={24} />
              </div>
              <h3 className="text-sm font-bold text-gray-800 mb-1">
                No Reports Available
              </h3>
              <p className="text-gray-500 text-xs mb-3">
                Upload and verify invoices to generate GST reconciliation
                reports.
              </p>
              <Button
                onClick={() => navigate("/invoices")}
                className="bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs h-7"
              >
                View Invoices
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Prepare chart data
  const chartData =
    financialData?.monthly_data?.map((m) => ({
      month: formatMonth(m.month),
      Purchase: Math.round(m.purchase_amount),
      Sales: Math.round(m.sales_amount),
      "Purchase GST": Math.round(m.purchase_gst),
      "Sales GST": Math.round(m.sales_gst),
    })) || [];

  const pieDataAmounts = [
    {
      name: "Purchases",
      value: financialData?.totals?.total_purchase || 0,
      color: "#EF4444",
    },
    {
      name: "Sales",
      value: financialData?.totals?.total_sales || 0,
      color: "#10B981",
    },
  ];

  const pieDataGST = [
    {
      name: "Purchase GST",
      value: financialData?.totals?.total_purchase_gst || 0,
      color: "#EF4444",
    },
    {
      name: "Sales GST",
      value: financialData?.totals?.total_sales_gst || 0,
      color: "#10B981",
    },
  ];

  return (
    <Layout>
      <div className="space-y-4" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-500 to-orange-500 bg-clip-text text-transparent animate-gradient">
              Financial Reports & Analytics
            </h1>
            <p className="text-gray-500 text-xs mt-0.5">
              GST reconciliation and business insights
            </p>
          </div>
          <div className="flex gap-2">
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-sm">
              <Sparkles size={10} className="mr-1" />
              AI Powered
            </Badge>
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm">
              <Calendar size={10} className="mr-1" />
              Real-time
            </Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gradient-to-r from-primary-50 to-orange-50 p-0.5">
            <TabsTrigger
              value="gst"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              <Calculator size={12} />
              GST Reports
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              <BarChart3 size={12} />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* GST Reports Tab */}
          <TabsContent value="gst" className="mt-3 space-y-3">
            {/* Month Selector and Download */}
            <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-bold text-gray-800">
                      GST Reconciliation
                    </h2>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {selectedMonth
                        ? formatMonth(selectedMonth)
                        : "Select a month"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={selectedMonth}
                      onValueChange={setSelectedMonth}
                    >
                      <SelectTrigger className="w-full sm:w-40 h-7 text-xs border-gray-300">
                        <div className="flex items-center gap-1.5">
                          <Calendar size={10} />
                          <SelectValue placeholder="Select month" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((month) => (
                          <SelectItem
                            key={month}
                            value={month}
                            className="text-xs"
                          >
                            {formatMonth(month)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={downloadReport}
                      disabled={!report}
                      className="h-7 text-xs bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-sm"
                    >
                      <Download size={10} className="mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {report && (
              <>
                {/* Summary Cards - Enhanced Gradient Version */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                  {/* Total Purchase */}
                  <Card className="border-0 overflow-hidden group relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-red-500 opacity-90 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <CardContent className="p-3 relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-white opacity-95">
                          Total Purchase
                        </span>
                        <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                          <ShoppingCart size={12} className="text-white" />
                        </div>
                      </div>
                      <div className="text-lg font-bold text-white">
                        {formatCurrency(report.total_purchase_amount)}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-[10px] text-white/80">
                          {report.purchase_invoices} invoices
                        </div>
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-white/20 backdrop-blur-sm text-white border-white/30"
                        >
                          GST: {formatCurrency(report.total_purchase_gst)}
                        </Badge>
                      </div>
                      {renderTaxSplit(report.purchase_tax_breakdown, "text-white")}
                    </CardContent>
                  </Card>

                  {/* Total Sales */}
                  <Card className="border-0 overflow-hidden group relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-500 opacity-90 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <CardContent className="p-3 relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-white opacity-95">
                          Total Sales
                        </span>
                        <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                          <Receipt size={12} className="text-white" />
                        </div>
                      </div>
                      <div className="text-lg font-bold text-white">
                        {formatCurrency(report.total_sales_amount)}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-[10px] text-white/80">
                          {report.sales_invoices} invoices
                        </div>
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-white/20 backdrop-blur-sm text-white border-white/30"
                        >
                          GST: {formatCurrency(report.total_sales_gst)}
                        </Badge>
                      </div>
                      {renderTaxSplit(report.sales_tax_breakdown, "text-white")}
                    </CardContent>
                  </Card>

                  {/* Net GST Payable/Receivable */}
                  <Card
                    className={`border-0 overflow-hidden group relative ${
                      report.net_gst_payable > 0
                        ? "border-l-4 border-l-gray-800"
                        : "border-l-4 border-l-gray-800"
                    }`}
                  >
                    <div
                      className={`absolute inset-0 ${
                        report.net_gst_payable > 0
                          ? "bg-gradient-to-br from-red-400 to-red-500"
                          : "bg-gradient-to-br from-gray-800 to-gray-500"
                      } opacity-90 group-hover:opacity-100 transition-opacity duration-300`}
                    ></div>
                    <CardContent className="p-3 relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-white opacity-95">
                          Net GST{" "}
                          {report.net_gst_payable > 0
                            ? "Payable"
                            : "Receivable"}
                        </span>
                        <div
                          className={`p-1.5 rounded-md ${
                            report.net_gst_payable > 0
                              ? "bg-red-500/30"
                              : "bg-green-500/30"
                          } backdrop-blur-sm`}
                        >
                          <Calculator size={12} className="text-white" />
                        </div>
                      </div>
                      <div
                        className={`text-lg font-bold ${
                          report.net_gst_payable > 0
                            ? "text-white"
                            : "text-white"
                        }`}
                      >
                        {report.net_gst_payable > 0 ? "+" : ""}
                        {formatCurrency(Math.abs(report.net_gst_payable))}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        {report.net_gst_payable > 0 ? (
                          <>
                            <div className="flex items-center gap-1">
                              <ChevronUp className="text-white" size={12} />
                              <span className="text-[10px] text-white font-medium">
                                To be paid
                              </span>
                            </div>
                            <div className="text-[10px] text-white/80">
                              (Payable to Govt.)
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-1">
                              <ChevronDown className="text-white" size={12} />
                              <span className="text-[10px] text-white font-medium">
                                Claimable
                              </span>
                            </div>
                            <div className="text-[10px] text-white/80">
                              (Input Tax Credit)
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* GST Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {/* Purchase GST Breakdown */}
                  <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                        <div className="p-1 rounded-md bg-gradient-to-br from-red-500 to-red-600">
                          <ShoppingCart size={12} className="text-white" />
                        </div>
                        Purchase GST Breakdown
                      </CardTitle>
                      <CardDescription className="text-xs text-gray-500">
                        GST analysis by tax rate
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {Object.keys(report.gst_breakdown.purchase).length ===
                      0 ? (
                        <div className="text-center py-4">
                          <div className="p-2 rounded-full bg-red-50 inline-block mb-2">
                            <ShoppingCart size={16} className="text-red-600" />
                          </div>
                          <p className="text-xs text-gray-500">
                            No purchase invoices for this period
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(report.gst_breakdown.purchase)
                            .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                            .map(([rate, data]) => (
                              <div
                                key={rate}
                                className="p-2 bg-gradient-to-br from-red-50/30 to-red-100/10 rounded-lg border border-red-200/50 hover:border-red-300 transition-colors"
                                data-testid={`purchase-gst-${rate}`}
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <Badge className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0 text-[10px] px-1.5 py-0">
                                    {rate}% GST
                                  </Badge>
                                  <span className="text-[10px] text-red-700/80">
                                    {data.count} invoices
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      Taxable Amount
                                    </span>
                                    <div className="text-xs font-medium text-gray-800">
                                      ₹
                                      {data.taxable_amount.toLocaleString(
                                        "en-IN",
                                      )}
                                    </div>
                                  </div>
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      GST Amount
                                    </span>
                                    <div className="text-xs font-medium text-red-700">
                                      ₹{data.gst_amount.toLocaleString("en-IN")}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      CGST
                                    </span>
                                    <div className="text-xs font-medium text-red-700">
                                      {formatCurrency(data.cgst || 0)}
                                    </div>
                                  </div>
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      SGST
                                    </span>
                                    <div className="text-xs font-medium text-red-700">
                                      {formatCurrency(data.sgst || 0)}
                                    </div>
                                  </div>
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      IGST
                                    </span>
                                    <div className="text-xs font-medium text-red-700">
                                      {formatCurrency(data.igst || 0)}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-1.5">
                                  <Progress
                                    value={
                                      (data.gst_amount /
                                        (report.total_purchase_gst || 1)) *
                                      100
                                    }
                                    className="h-1 bg-red-100"
                                    indicatorClassName="bg-gradient-to-r from-red-500 to-red-600"
                                  />
                                  <div className="text-[10px] text-red-700/80 text-right mt-0.5">
                                    {(
                                      (data.gst_amount /
                                        (report.total_purchase_gst || 1)) *
                                      100
                                    ).toFixed(1)}
                                    %
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Sales GST Breakdown */}
                  <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                        <div className="p-1 rounded-md bg-gradient-to-br from-green-500 to-green-600">
                          <Receipt size={12} className="text-white" />
                        </div>
                        Sales GST Breakdown
                      </CardTitle>
                      <CardDescription className="text-xs text-gray-500">
                        GST analysis by tax rate
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {Object.keys(report.gst_breakdown.sales).length === 0 ? (
                        <div className="text-center py-4">
                          <div className="p-2 rounded-full bg-green-50 inline-block mb-2">
                            <Receipt size={16} className="text-green-600" />
                          </div>
                          <p className="text-xs text-gray-500">
                            No sales invoices for this period
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(report.gst_breakdown.sales)
                            .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                            .map(([rate, data]) => (
                              <div
                                key={rate}
                                className="p-2 bg-gradient-to-br from-green-50/30 to-green-100/10 rounded-lg border border-green-200/50 hover:border-green-300 transition-colors"
                                data-testid={`sales-gst-${rate}`}
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <Badge className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 text-[10px] px-1.5 py-0">
                                    {rate}% GST
                                  </Badge>
                                  <span className="text-[10px] text-green-700/80">
                                    {data.count} invoices
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      Taxable Amount
                                    </span>
                                    <div className="text-xs font-medium text-gray-800">
                                      ₹
                                      {data.taxable_amount.toLocaleString(
                                        "en-IN",
                                      )}
                                    </div>
                                  </div>
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      GST Amount
                                    </span>
                                    <div className="text-xs font-medium text-green-700">
                                      ₹{data.gst_amount.toLocaleString("en-IN")}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      CGST
                                    </span>
                                    <div className="text-xs font-medium text-green-700">
                                      {formatCurrency(data.cgst || 0)}
                                    </div>
                                  </div>
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      SGST
                                    </span>
                                    <div className="text-xs font-medium text-green-700">
                                      {formatCurrency(data.sgst || 0)}
                                    </div>
                                  </div>
                                  <div className="space-y-0.5">
                                    <span className="text-[10px] text-gray-500">
                                      IGST
                                    </span>
                                    <div className="text-xs font-medium text-green-700">
                                      {formatCurrency(data.igst || 0)}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-1.5">
                                  <Progress
                                    value={
                                      (data.gst_amount /
                                        (report.total_sales_gst || 1)) *
                                      100
                                    }
                                    className="h-1 bg-green-100"
                                    indicatorClassName="bg-gradient-to-r from-green-500 to-green-600"
                                  />
                                  <div className="text-[10px] text-green-700/80 text-right mt-0.5">
                                    {(
                                      (data.gst_amount /
                                        (report.total_sales_gst || 1)) *
                                      100
                                    ).toFixed(1)}
                                    %
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Reconciliation Formula */}
                <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-bold text-gray-800">
                      GST Reconciliation Formula
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap items-center justify-center gap-3 py-3">
                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-500 mb-0.5">
                          Sales GST
                        </div>
                        <div className="text-sm font-bold bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                          ₹{report.total_sales_gst.toLocaleString("en-IN")}
                        </div>
                      </div>

                      <div className="text-sm font-bold text-gray-800">-</div>

                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-500 mb-0.5">
                          Purchase GST
                        </div>
                        <div className="text-sm font-bold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                          ₹{report.total_purchase_gst.toLocaleString("en-IN")}
                        </div>
                      </div>

                      <div className="text-sm font-bold text-gray-800">=</div>

                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-500 mb-0.5">
                          {report.net_gst_payable > 0
                            ? "Net Payable"
                            : "Net Receivable"}
                        </div>
                        <div
                          className={`text-sm font-bold ${report.net_gst_payable > 0 ? "bg-gradient-to-r from-red-500 to-red-600" : "bg-gradient-to-r from-green-500 to-green-600"} bg-clip-text text-transparent`}
                        >
                          {report.net_gst_payable > 0 ? "+" : ""}₹
                          {Math.abs(report.net_gst_payable).toLocaleString(
                            "en-IN",
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Note for CA Firms */}
                <Alert className="bg-gradient-to-r from-primary-50 to-orange-50 border border-primary-200">
                  <div>
                    <AlertDescription className="text-xs text-gray-600">
                      <span className="font-medium text-gray-700">
                        Note for CA Firms:
                      </span>{" "}
                      This report shows only verified invoices. Ensure all
                      invoices are verified before filing GST returns.
                    </AlertDescription>
                  </div>
                </Alert>
              </>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-3 space-y-3">
            {/* Summary Totals */}
            {financialData?.totals && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {[
                  {
                    label: "Total Purchases",
                    value: financialData.totals.total_purchase,
                    gradient: "from-red-400 to-red-500",
                    icon: ShoppingCart,
                    hoverGradient: "hover:from-red-500 hover:to-red-600",

                    changeColor: "text-red-600",
                  },
                  {
                    label: "Total Sales",
                    value: financialData.totals.total_sales,
                    gradient: "from-green-400 to-green-500",
                    icon: Receipt,
                    hoverGradient: "hover:from-green-500 hover:to-green-600",

                    changeColor: "text-green-600",
                  },
                  {
                    label: "Purchase GST",
                    value: financialData.totals.total_purchase_gst,
                    gradient: "from-orange-400 to-orange-500",
                    icon: TrendingDown,
                    hoverGradient: "hover:from-orange-500 hover:to-orange-600",

                    changeColor: "text-orange-600",
                  },
                  {
                    label: "Sales GST",
                    value: financialData.totals.total_sales_gst,
                    gradient: "from-emerald-400 to-emerald-500",
                    icon: TrendingUp,
                    hoverGradient:
                      "hover:from-emerald-500 hover:to-emerald-600",

                    changeColor: "text-emerald-600",
                  },
                ].map((stat, index) => (
                  <Card
                    key={index}
                    className={`border-0 overflow-hidden group transition-all duration-300 text-white relative`}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} ${stat.hoverGradient} opacity-90 group-hover:opacity-100 transition-opacity duration-300`}
                    ></div>

                    <CardContent className="p-3 relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                          <stat.icon size={12} />
                        </div>
                      </div>
                      <div className="text-xs font-medium opacity-95 mb-0.5">
                        {stat.label}
                      </div>
                      <div className="text-lg font-bold">
                        {formatCurrency(stat.value)}
                      </div>
                      <div className="text-[10px] opacity-90 mt-0.5">
                        Cumulative amount
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Bar Chart - Month-wise Comparison */}
            {chartData.length > 0 && (
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <div className="p-1 rounded-md bg-gradient-to-br from-primary-500 to-orange-500">
                      <BarChart3 size={12} className="text-white" />
                    </div>
                    Month-wise Sales vs Purchase
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500">
                    Comparative analysis over time
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="#e5e7eb"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 10 }}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={40}
                        />
                        <YAxis
                          tickFormatter={(value) =>
                            `₹${(value / 1000).toFixed(0)}K`
                          }
                          tick={{ fontSize: 10 }}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `₹${value.toLocaleString("en-IN")}`,
                            "",
                          ]}
                          contentStyle={{
                            borderRadius: "6px",
                            border: "1px solid #e5e7eb",
                            fontSize: "11px",
                          }}
                        />
                        <Bar
                          dataKey="Purchase"
                          fill="url(#redGradient)"
                          radius={[2, 2, 0, 0]}
                          name="Purchases"
                        />
                        <Bar
                          dataKey="Sales"
                          fill="url(#greenGradient)"
                          radius={[2, 2, 0, 0]}
                          name="Sales"
                        />
                        <defs>
                          <linearGradient
                            id="redGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#ef4444"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="100%"
                              stopColor="#dc2626"
                              stopOpacity={0.8}
                            />
                          </linearGradient>
                          <linearGradient
                            id="greenGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#10b981"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="100%"
                              stopColor="#059669"
                              stopOpacity={0.8}
                            />
                          </linearGradient>
                        </defs>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pie Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Amount Distribution Pie Chart */}
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <div className="p-1 rounded-md bg-gradient-to-br from-purple-500 to-pink-500">
                      <PieChart size={12} className="text-white" />
                    </div>
                    Amount Distribution
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500">
                    Sales vs Purchase amounts
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={pieDataAmounts}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {pieDataAmounts.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [
                            `₹${value.toLocaleString("en-IN")}`,
                            "",
                          ]}
                          contentStyle={{
                            borderRadius: "6px",
                            border: "1px solid #e5e7eb",
                            fontSize: "11px",
                          }}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* GST Distribution Pie Chart */}
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <div className="p-1 rounded-md bg-gradient-to-br from-cyan-500 to-blue-500">
                      <PieChart size={12} className="text-white" />
                    </div>
                    GST Distribution
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500">
                    Sales GST vs Purchase GST
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={pieDataGST}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {pieDataGST.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [
                            `₹${value.toLocaleString("en-IN")}`,
                            "",
                          ]}
                          contentStyle={{
                            borderRadius: "6px",
                            border: "1px solid #e5e7eb",
                            fontSize: "11px",
                          }}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* GST Trend Bar Chart */}
            {chartData.length > 0 && (
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <div className="p-1 rounded-md bg-gradient-to-br from-primary-500 to-orange-500">
                      <TrendingUp size={12} className="text-white" />
                    </div>
                    GST Trend Analysis
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500">
                    Month-wise GST paid vs collected
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="#e5e7eb"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 10 }}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={40}
                        />
                        <YAxis
                          tickFormatter={(value) =>
                            `₹${(value / 1000).toFixed(0)}K`
                          }
                          tick={{ fontSize: 10 }}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `₹${value.toLocaleString("en-IN")}`,
                            "",
                          ]}
                          contentStyle={{
                            borderRadius: "6px",
                            border: "1px solid #e5e7eb",
                            fontSize: "11px",
                          }}
                        />
                        <Bar
                          dataKey="Purchase GST"
                          fill="url(#orangeGradient)"
                          radius={[2, 2, 0, 0]}
                          name="Purchase GST"
                        />
                        <Bar
                          dataKey="Sales GST"
                          fill="url(#emeraldGradient)"
                          radius={[2, 2, 0, 0]}
                          name="Sales GST"
                        />
                        <defs>
                          <linearGradient
                            id="orangeGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#f97316"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="100%"
                              stopColor="#ea580c"
                              stopOpacity={0.8}
                            />
                          </linearGradient>
                          <linearGradient
                            id="emeraldGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#10b981"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="100%"
                              stopColor="#059669"
                              stopOpacity={0.8}
                            />
                          </linearGradient>
                        </defs>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Data State */}
            {(!chartData || chartData.length === 0) && (
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardContent className="p-6 text-center">
                  <div className="p-3 rounded-full bg-gradient-to-br from-primary-50 to-orange-50 inline-block mb-3">
                    <BarChart3 className="text-primary-600" size={22} />
                  </div>
                  <h3 className="text-sm font-bold text-gray-800 mb-1">
                    No Analytics Data Available
                  </h3>
                  <p className="text-gray-500 text-xs mb-3">
                    Upload invoices to see financial analytics and charts.
                  </p>
                  <Button
                    onClick={() => navigate("/invoices")}
                    className="bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs h-7 shadow-sm"
                  >
                    <Eye size={10} className="mr-1" />
                    View Invoices
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
